import { EncodedCharacters } from './../../../Core/@types/global/tracking';
import { TrafficSourceForwardingMethods } from '../../TS/UtmTracking/TrafficSourceForwardingMethods';
import { UtmInfo } from '../../TS/UtmTracking/UtmInfo';
import { UtmFields } from '../../TS/UtmTracking/UtmFields';
import { UtmMediumValues, UtmSourceValues } from '../../TS/UtmTracking/UtmAllowedValues';

class TrackingSpecialPartnershipHandler {
	private _gaCrossDomainTrackingLinker = '';

	public get gaCrossDomainTrackingLinker(): string {
		return this._gaCrossDomainTrackingLinker;
	}

	public prepareGACrossDomainTrackingLinker(): void {
		if (!window.ga) {
			return;
		}

		window.ga((tracker) => {
			this._gaCrossDomainTrackingLinker = tracker.get('linkerParam');
		});
	}

	public applyPartnershipSpecificUrlAdjustments(element: HTMLElement, url: URL): URL {
		if (this.doesCampaignRequireAdditionalUrlAdjustments()) {
			url.href = this.enrichHrefWithGACrossDomainTrackingLinker(url.href);
		}

		url.href = this.enrichHrefWithUTMInformation(element, url);

		return url;
	}

	private enrichHrefWithGACrossDomainTrackingLinker(href: string): string {
		const gaCrossDomainTrackingLinker = this.gaCrossDomainTrackingLinker;
		if (!gaCrossDomainTrackingLinker) {
			return href;
		}

		if (href.includes(EncodedCharacters.QUESTION_MARK)) {
			return `${href}${EncodedCharacters.AMPERSAND}${gaCrossDomainTrackingLinker}`;
		}

		return `${href}${EncodedCharacters.QUESTION_MARK}${gaCrossDomainTrackingLinker}`;
	}

	private enrichHrefWithUTMInformation(element: HTMLElement, url: URL): string {
		const method = this.getCampaignTrackMethod();

		if (method === TrafficSourceForwardingMethods.IU.valueOf()) {
			return this.translateHrefBasedOnIUMethod(element, url.href);
		}

		if (method === TrafficSourceForwardingMethods.SOMMET.valueOf()) {
			return this.translateHrefBasedOnSommetMethod(url);
		}

		if (
			method === TrafficSourceForwardingMethods.IUTM.valueOf() ||
			method === TrafficSourceForwardingMethods.GUS.valueOf() ||
			method === TrafficSourceForwardingMethods.WILEY.valueOf()
		) {
			return this.translateHrefBasedOnIUTMMethod(url);
		}

		return url.href;
	}

	private translateHrefBasedOnIUMethod(element: HTMLElement, href: string): string {
		const utmInfo = this.getUtmInfo();

		if (!utmInfo || !this.isValidIUCampaign(utmInfo) || !this.isIUOrganisation()) {
			return href;
		}

		const utmInfoUpdated = this.updateUtmInfo(utmInfo);

		return this.transformHrefForIUCampaign(utmInfoUpdated, element, href);
	}

	private translateHrefBasedOnSommetMethod(url: URL): string {
		const utmInfo = this.getUtmInfo();
		const campaignKey = UtmFields.UTM_CAMPAIGN.valueOf();
		let campaignValue = 'SEO';

		if (utmInfo && campaignKey in utmInfo) {
			campaignValue = utmInfo[campaignKey as keyof UtmInfo] as string;
		}

		const query = `${EncodedCharacters.AMPERSAND}${campaignKey}=${campaignValue}`;
		const enrichedHref = this.addQueryToTargetUrl(url, query);

		return enrichedHref;
	}

	private translateHrefBasedOnIUTMMethod(url: URL): string {
		const utmInfo = this.getUtmInfo();
		if (!utmInfo) {
			return url.href;
		}

		let query = '';

		for (const [key, value] of Object.entries(utmInfo)) {
			const updatedKeyName = `i${key}`;
			query = `${query}${EncodedCharacters.AMPERSAND}${updatedKeyName}=${value}`;
		}

		const enrichedHref = this.addQueryToTargetUrl(url, query);
		return enrichedHref;
	}

	private addQueryToTargetUrl(url: URL, query: string): string {
		const searchParametersObject: any = {};
		const searchQuery = url.search;

		const searchParams = new URLSearchParams(searchQuery.substring(1));
		for (const [key, value] of searchParams.entries()) {
			searchParametersObject[key as keyof URLSearchParams] = value;
		}

		const target = searchParametersObject['target'] as string;
		if (!target) {
			return url.href;
		}

		const targetParameterAsNumber = Number.parseInt(target, 10);
		if(!Number.isNaN(targetParameterAsNumber)) {
			// If the Target is a number, then it is a linkId and can't be enriched.
			// TODO: CITA-20307 - Rework the entire system that is currently responsible for generating the tracking url.
			return url.href;
		}

		const targetUrl = new URL(target);
		const encodedHref = encodeURIComponent(targetUrl.href);
		const encodedSearch = encodeURIComponent(targetUrl.search);

		const enrichedTargetUrl =
			targetUrl.search === ''
				? `${encodedHref}${query.replace(EncodedCharacters.AMPERSAND, EncodedCharacters.QUESTION_MARK)}`
				: encodedHref.replace(encodedSearch, `${encodedSearch}${query}`);

		url.search = `?target=${enrichedTargetUrl}`;

		return url.href;
	}

	private doesCampaignRequireAdditionalUrlAdjustments(): boolean {
		return this.isCPLCampaign() || this.isUncappedCampaign() || this.isIUOrganisation();
	}

	private isCPLCampaign(): boolean {
		const campaignInfoElement = document.getElementById('js-campaignInformationExposer');
		if (!campaignInfoElement) {
			return false;
		}

		const campaignName = campaignInfoElement.dataset.cn || '';
		return campaignName.toLowerCase().includes('cpl');
	}

	private isUncappedCampaign(): boolean {
		const campaignInfoElement = document.getElementById('js-campaignInformationExposer');
		if (!campaignInfoElement) {
			return false;
		}

		return campaignInfoElement.dataset.cic === '0';
	}

	private isIUOrganisation(): boolean {
		const relevantOrganisationIds = ['421', '21013', '21012'];
		const campaignInfoElement =
			document.getElementById('OrganisationInformation') || document.getElementById('OrganisationCoverImage');

		if (!campaignInfoElement) {
			return false;
		}

		return relevantOrganisationIds.includes(campaignInfoElement.dataset.organisation_id || '');
	}

	private transformHrefForIUCampaign(utmInfo: UtmInfo, element: HTMLElement, href: string): string {
		const existingAdref = `adref${EncodedCharacters.COLON}SEO`;

		if (!href.includes(existingAdref)) {
			return href;
		}

		let newAdref = `adref${EncodedCharacters.COLON}${utmInfo.utm_medium}`;
		if (utmInfo.utm_campaign) {
			newAdref = `${newAdref}${decodeURIComponent('%7E')}${utmInfo.utm_campaign}`;
		}

		href = href.replace(existingAdref, newAdref);

		const description = element.dataset.description;
		if (description) {
			element.dataset.description = description.replace(existingAdref, `adref:${utmInfo.utm_medium}`);
		}

		return href;
	}

	public updateUtmInfo(utmInfo: UtmInfo): UtmInfo {
		const utmMedium = utmInfo.utm_medium;
		const utmSource = utmInfo.utm_source;
		let updatedUtmMedium = utmMedium;

		const isCpc = utmMedium === UtmMediumValues.CPC || utmMedium === UtmMediumValues.CPC_SOCIAL;

		if (utmMedium === UtmMediumValues.EMAIL && utmSource === UtmSourceValues.CIO) {
			updatedUtmMedium = UtmMediumValues.EMAIL;
		}

		if (isCpc && utmSource === UtmSourceValues.GOOGLE) {
			updatedUtmMedium = UtmMediumValues.SEA;
		}

		if (isCpc && utmSource === UtmSourceValues.TIKTOK) {
			updatedUtmMedium = UtmSourceValues.TIKTOK;
		}

		if (isCpc && utmSource === UtmSourceValues.FACEBOOK) {
			updatedUtmMedium = UtmSourceValues.FACEBOOK;
		}

		if (isCpc && utmSource === UtmSourceValues.INSTAGRAM) {
			updatedUtmMedium = UtmSourceValues.INSTAGRAM;
		}

		return {
			...utmInfo,
			utm_medium: updatedUtmMedium
		}
	}

	public isValidIUCampaign(utmInfo: UtmInfo): boolean {
		const allowedUtmMediumValues = Object.values(UtmMediumValues).map(v => v.valueOf());

		if (!utmInfo.utm_medium) {
			return false;
		}

		return allowedUtmMediumValues.includes(utmInfo.utm_medium)
			|| document.referrer === 'scholarship.mastersportal.com';
	}

	private getUtmInfo(): UtmInfo | undefined {
		const storedUtmObject = window.Loot.DataStorage.retrieve('utmInfo') as string;
		if (!storedUtmObject) {
			return;
		}

		return JSON.parse(storedUtmObject) as UtmInfo;
	}

	private getCampaignTrackMethod(): string | undefined {
		const anyWindow = window as any;

		return anyWindow['traffic_source_forward_method'] as string | undefined;
	}
}

export default new TrackingSpecialPartnershipHandler();
